import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach token from localStorage as Authorization header (fallback for cross-site cookies)
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("pp_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  // Attach the user's language on every GET so backend can serve translated
  // content — EXCEPT on admin routes and explicit `?raw=true` reads. Admin
  // forms must edit the source-of-truth (English) fields; if we let the
  // translated overlay load into the form, Save would overwrite the base
  // fields with the overlay and silently corrupt the record.
  try {
    const url = cfg.url || "";
    const method = (cfg.method || "get").toLowerCase();
    const isAdmin = url.includes("/admin/");
    const isRaw = cfg.params && cfg.params.raw === true;
    const lang = localStorage.getItem("pp.lang");
    if (lang && lang !== "en" && method === "get" && !isAdmin && !isRaw) {
      cfg.params = { ...(cfg.params || {}), lang };
    }
  } catch { /* ignore */ }
  return cfg;
});

export function fmtPrice(n, currency = "AED") {
  if (n == null) return "";
  return `${currency} ${Number(n).toLocaleString("en-US")}`;
}

export function fmtCompact(n) {
  if (n == null) return "";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return `${n}`;
}

export function resolveImage(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  return `${API}/files/${pathOrUrl}`;
}

/* --------------------------------------------------------------------------
 * cachedGet — stale-while-revalidate for public list pages.
 * Reads from sessionStorage first (instant paint on repeat visits), then
 * refreshes in the background. Bypassed on admin/authenticated requests.
 * Only used explicitly by the pages that opt in (/properties, /areas) so
 * we never risk stale data on the admin panel or on record-detail pages.
 * ------------------------------------------------------------------------ */
const SWR_TTL_MS = 5 * 60 * 1000;   // 5 minutes

function _swrKey(url, params) {
  const p = params ? Object.entries(params).sort().map(([k, v]) => `${k}=${v}`).join("&") : "";
  return `pp.swr:${url}?${p}`;
}
function _readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (Date.now() - p.t > SWR_TTL_MS) return null;
    return p.d;
  } catch { return null; }
}
function _writeCache(key, data) {
  try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: data })); }
  catch { /* quota / private-mode */ }
}

/**
 * cachedGet(url, opts) — returns { data, isStale, refresh }.
 *   - `data`: cached data if present (instant), else fresh data.
 *   - `isStale`: true if `data` came from cache.
 *   - `refresh`: Promise<freshData> for the background revalidation.
 */
export async function cachedGet(url, opts = {}) {
  const key = _swrKey(url, opts.params);
  const cached = _readCache(key);
  const network = api.get(url, opts).then((r) => {
    _writeCache(key, r.data);
    return r.data;
  });
  if (cached) {
    network.catch(() => {});
    return { data: cached, isStale: true, refresh: network };
  }
  const data = await network;
  return { data, isStale: false, refresh: Promise.resolve(data) };
}
